import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './context/AuthContext'
import { Loader2, UserCheck, X } from 'lucide-react'

// ── Impersonation banner (shown at top when superadmin is logged in as someone else)
function ImpersonationBanner() {
  const { impersonating, stopImpersonation, currentUser } = useAuth()
  if (!impersonating) return null
  return (
    <div className="fixed top-0 left-0 right-0 z-[200] flex items-center justify-between px-4 py-2 text-xs font-semibold"
      style={{ background: '#4a9eff', color: '#000' }}>
      <div className="flex items-center gap-2">
        <UserCheck size={14} />
        <span>Viewing as <strong>{currentUser?.name}</strong> ({currentUser?.role}) — Super Admin mode</span>
      </div>
      <button onClick={stopImpersonation}
        className="flex items-center gap-1.5 px-3 py-1 rounded-md font-bold transition-all"
        style={{ background: 'rgba(0,0,0,0.15)' }}
        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.3)')}
        onMouseLeave={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.15)')}>
        <X size={12} /> Stop Impersonating
      </button>
    </div>
  )
}

// ── Auth pages ────────────────────────────────────────────────
import Login        from './pages/Login'
import AuthCallback from './pages/AuthCallback'
import Unauthorized from './pages/Unauthorized'

// ── Role dashboards ───────────────────────────────────────────
import AdminDashboard    from './pages/admin/AdminDashboard'
import FounderDashboard  from './pages/founder/FounderDashboard'
import EmployeeDashboard from './pages/employee/EmployeeDashboard'

// ── Existing ERP pages ────────────────────────────────────────
import Layout       from './components/Layout/Layout'
import Dashboard    from './pages/Dashboard'
import Clients      from './pages/Clients'
import Projects     from './pages/Projects'
import Pipeline     from './pages/Pipeline'
import Campaigns    from './pages/Campaigns'
import CalendarPage from './pages/CalendarPage'
import Invoices     from './pages/Invoices'
import Expenses     from './pages/Expenses'
import Team         from './pages/Team'
import Tasks        from './pages/Tasks'
import AISuite      from './pages/AISuite'
import Attendance   from './pages/Attendance'
import Reports      from './pages/Reports'
import Settings     from './pages/Settings'

import { getDashboardPath } from './services/auth.service'

// ── Splash while session restores ────────────────────────────
function AppLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#000' }}>
      <div className="flex flex-col items-center gap-3">
        <Loader2 size={28} className="animate-spin" style={{ color: '#ffc600' }} />
        <p className="text-sm" style={{ color: '#555', fontFamily: 'DM Sans, sans-serif' }}>
          Loading Clickbait…
        </p>
      </div>
    </div>
  )
}

// ── Must be logged in ─────────────────────────────────────────
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading) return <AppLoader />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <>{children}</>
}

// ── /login is inaccessible once logged in ────────────────────
function PublicOnly({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, currentUser } = useAuth()
  if (isLoading) return <AppLoader />
  if (isAuthenticated && currentUser) {
    return <Navigate to={getDashboardPath(currentUser.role)} replace />
  }
  return <>{children}</>
}

// ── Role-specific routes ──────────────────────────────────────
function RequireRole({ roles, children }: { roles: string[]; children: React.ReactNode }) {
  const { currentUser, isLoading } = useAuth()
  if (isLoading) return <AppLoader />
  if (!currentUser) return <Navigate to="/login" replace />
  if (!roles.includes(currentUser.role)) return <Navigate to="/unauthorized" replace />
  return <>{children}</>
}

export default function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>

        {/* ── Login (public only) ──────────────────────── */}
        <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />

        {/* ── OAuth callback ────────────────────────────── */}
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* ── Unauthorized ─────────────────────────────── */}
        <Route path="/unauthorized" element={<Unauthorized />} />

        {/* ── Super Admin ──────────────────────────────── */}
        <Route path="/admin/*" element={
          <RequireRole roles={['superadmin']}>
            <AdminDashboard />
          </RequireRole>
        } />

        {/* ── Founder ──────────────────────────────────── */}
        <Route path="/founder/*" element={
          <RequireRole roles={['founder']}>
            <FounderDashboard />
          </RequireRole>
        } />

        {/* ── Employee ─────────────────────────────────── */}
        <Route path="/employee/*" element={
          <RequireRole roles={['employee']}>
            <EmployeeDashboard />
          </RequireRole>
        } />

        {/* ── Full ERP (superadmin + founder only) ────────── */}
        <Route path="/" element={<RequireRole roles={['superadmin', 'founder']}><Layout /></RequireRole>}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard"  element={<Dashboard />} />
          <Route path="clients"    element={<Clients />} />
          <Route path="projects"   element={<Projects />} />
          <Route path="pipeline"   element={<Pipeline />} />
          <Route path="campaigns"  element={<Campaigns />} />
          <Route path="calendar"   element={<CalendarPage />} />
          <Route path="invoices"   element={<Invoices />} />
          <Route path="expenses"   element={<Expenses />} />
          <Route path="team"       element={<Team />} />
          <Route path="tasks"      element={<Tasks />} />
          <Route path="ai-suite"   element={<AISuite />} />
          <Route path="attendance" element={<Attendance />} />
          <Route path="reports"    element={<Reports />} />
          <Route path="settings"   element={<Settings />} />
        </Route>

        {/* ── Catch-all → login ─────────────────────────── */}
        <Route path="*" element={<Navigate to="/login" replace />} />

      </Routes>
      <ImpersonationBanner />
    </BrowserRouter>
  )
}
